import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { isRenderMode } from '../renderMode';

const RoleContext = createContext(null);

export function RoleProvider({ children, user }) {
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState(new Set());
  const [allPermissions, setAllPermissions] = useState([]); // Full permission objects
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [defaultLandingPath, setDefaultLandingPath] = useState(null);

  // Fetch user's role and permissions
  const fetchPermissions = useCallback(async () => {
    if (!user?.id) {
      setUserRole(null);
      setPermissions(new Set());
      setAllPermissions([]);
      setIsAdmin(false);
      setDefaultLandingPath(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Per-user default landing page (best-effort; absence is fine)
      supabase
        .from('user_preferences')
        .select('default_landing_path')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => setDefaultLandingPath(data?.default_landing_path || null))
        .catch(() => setDefaultLandingPath(null));

      // Get user's role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select(`
          role_id,
          roles (
            id,
            name,
            description,
            is_system
          )
        `)
        .eq('user_id', user.id)
        .single();

      if (roleError && roleError.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which is OK for new users
        console.error('Error fetching user role:', roleError);
      }

      const role = roleData?.roles || null;
      setUserRole(role);
      setIsAdmin(role?.name === 'Admin');

      // If no role assigned, user has no permissions (except home)
      if (!role) {
        setPermissions(new Set(['page:home']));
        setAllPermissions([{ resource_type: 'page', resource_key: 'home', resource_name: 'The Homestead', category: 'Home' }]);
        setLoading(false);
        return;
      }

      // Get permissions for this role
      const { data: permData, error: permError } = await supabase
        .rpc('get_user_permissions', { p_user_id: user.id });

      if (permError) {
        console.error('Error fetching permissions:', permError);
        setPermissions(new Set(['page:home']));
        setAllPermissions([]);
        setLoading(false);
        return;
      }

      // Build permission set for O(1) lookup
      const permSet = new Set();
      permSet.add('page:home'); // Everyone can access home

      (permData || []).forEach(p => {
        permSet.add(`${p.resource_type}:${p.resource_key}`);
      });

      setPermissions(permSet);
      setAllPermissions(permData || []);

    } catch (err) {
      console.error('Error in fetchPermissions:', err);
      setPermissions(new Set(['page:home']));
      setAllPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load permissions on mount and when user changes
  useEffect(() => {
    // Export pipeline: no login, no permission fetch — grant access so the report
    // renders. (Render mode only ever mounts the Round Up Report, nothing else.)
    if (isRenderMode) { setIsAdmin(true); setLoading(false); return; }
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has access to a specific resource
  const hasAccess = useCallback((type, key) => {
    // Admin has access to everything
    if (isAdmin) return true;

    // Home is always accessible
    if (type === 'page' && key === 'home') return true;

    // Check permission set
    return permissions.has(`${type}:${key}`);
  }, [permissions, isAdmin]);

  // Check if user has access to any resource in a category
  const hasAccessToCategory = useCallback((category) => {
    if (isAdmin) return true;
    return allPermissions.some(p => p.category === category);
  }, [allPermissions, isAdmin]);

  // Get all permissions for a specific type
  const getPermissionsByType = useCallback((type) => {
    return allPermissions.filter(p => p.resource_type === type);
  }, [allPermissions]);

  const value = useMemo(() => ({
    user,
    userRole,
    permissions,
    allPermissions,
    loading,
    isAdmin,
    defaultLandingPath,
    hasAccess,
    hasAccessToCategory,
    getPermissionsByType,
    refetch: fetchPermissions
  }), [user, userRole, permissions, allPermissions, loading, isAdmin, defaultLandingPath, hasAccess, hasAccessToCategory, getPermissionsByType, fetchPermissions]);

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}

export default RoleContext;
